import { CalendarDate } from "@internationalized/date";
import { CheckCircle, Tool02, XClose } from "@untitledui/icons";
import type { DateValue } from "react-aria-components";
import { DatePicker } from "@/components/application/date-picker/date-picker";
import { Input } from "@/components/base/input/input";
import ModalBase from "@/components/ui/modal/modal-base";
import type { PreventiveMaintenanceRequest } from "@/features/vehicle/types";

const ISO_DATE_REGEX = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

/** Converte string de data (dd/MM/yyyy ou yyyy-MM-dd) para DateValue do DatePicker. */
function parseDateStringToDateValue(str: string): DateValue | null {
	const s = str?.trim();
	if (!s) {
		return null;
	}
	// yyyy-MM-dd (formato da API ao editar)
	const isoMatch = ISO_DATE_REGEX.exec(s);
	if (isoMatch) {
		const y = Number.parseInt(isoMatch[1], 10);
		const m = Number.parseInt(isoMatch[2], 10);
		const d = Number.parseInt(isoMatch[3], 10);
		if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
			return new CalendarDate(y, m, d);
		}
		return null;
	}
	// dd/MM/yyyy
	const parts = s.replace(/\D/g, "");
	if (parts.length < 8) {
		return null;
	}
	const d = Number.parseInt(parts.slice(0, 2), 10);
	const m = Number.parseInt(parts.slice(2, 4), 10);
	const y = Number.parseInt(parts.slice(4, 8), 10);
	if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) {
		return null;
	}
	if (m < 1 || m > 12 || d < 1 || d > 31) {
		return null;
	}
	return new CalendarDate(y, m, d);
}

function formatDateValueToDdMmYyyy(value: DateValue | null): string {
	if (!value) {
		return "";
	}
	const d = String(value.day).padStart(2, "0");
	const m = String(value.month).padStart(2, "0");
	const y = value.year;
	return `${d}/${m}/${y}`;
}

/** Formata quilometragem: ponto como separador de milhar (a partir de 1.000) e " km" no final. */
function formatKilometersDisplay(km: number): string {
	if (km === 0) {
		return "";
	}
	return `${km.toLocaleString("pt-BR")} km`;
}

/** Extrai apenas dígitos do valor digitado e retorna o número para armazenar. */
function parseKilometersInput(value: string): number {
	const digits = value.replace(/\D/g, "");
	if (digits === "") {
		return 0;
	}
	const n = Number.parseInt(digits, 10);
	return Number.isNaN(n) ? 0 : n;
}

export type PreventiveMaintenanceFormData = PreventiveMaintenanceRequest;

type PreventiveMaintenanceModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onSave: (data: PreventiveMaintenanceRequest) => Promise<void>;
	formData: PreventiveMaintenanceFormData;
	onChange: (
		field: keyof PreventiveMaintenanceFormData,
		value: string | number
	) => void;
	isSaving?: boolean;
	alert?: {
		type: "success" | "error";
		title: string;
		description?: string;
	} | null;
	onAlertClose?: () => void;
	fieldErrors?: Partial<Record<keyof PreventiveMaintenanceFormData, string>>;
	mode: "add" | "edit";
};

export const PreventiveMaintenanceModal = ({
	isOpen,
	onClose,
	onSave,
	formData,
	onChange,
	isSaving = false,
	alert = null,
	onAlertClose,
	fieldErrors = {},
	mode,
}: PreventiveMaintenanceModalProps) => {
	const handleSubmit = () =>
		onSave({
			dateOfLastPreventive: formData.dateOfLastPreventive,
			kilometersOfLastPreventive: formData.kilometersOfLastPreventive,
			dateOfNextPreventive: formData.dateOfNextPreventive,
		});

	return (
		<ModalBase
			actions={[
				{
					label: "Cancelar",
					onClick: onClose,
					color: "secondary",
					disabled: isSaving,
				},
				{
					label: mode === "add" ? "Adicionar" : "Atualizar",
					onClick: handleSubmit,
					color: "primary",
					disabled: isSaving,
				},
			]}
			description={
				mode === "add"
					? "Registre a última preventiva e a próxima prevista"
					: "Atualize as datas e quilometragem da preventiva"
			}
			height="auto"
			icon={<Tool02 className="size-6" />}
			iconColor="brand"
			isOpen={isOpen}
			onClose={onClose}
			title={mode === "add" ? "Nova preventiva" : "Editar preventiva"}
			width={480}
		>
			<div className="space-y-4 pb-6">
				{alert ? (
					<div
						className={`flex w-full flex-col gap-2 rounded-xl border p-4 ${
							alert.type === "success"
								? "border-green-200 bg-green-50"
								: "border-red-200 bg-red-50"
						}`}
					>
						<div className="flex w-full justify-between">
							<div className="flex items-start gap-3">
								{alert.type === "success" ? (
									<CheckCircle className="mt-0.5 size-5 shrink-0 text-green-600" />
								) : (
									<XClose className="mt-0.5 size-5 shrink-0 text-red-600" />
								)}
								<div className="flex flex-col gap-1">
									<h5
										className={`font-semibold text-sm ${
											alert.type === "success"
												? "text-green-800"
												: "text-red-800"
										}`}
									>
										{alert.title}
									</h5>
									{alert.description ? (
										<p
											className={`text-sm ${
												alert.type === "success"
													? "text-green-700"
													: "text-red-700"
											}`}
										>
											{alert.description}
										</p>
									) : null}
								</div>
							</div>
							{alert.type === "error" ? (
								<button
									aria-label="Fechar alerta"
									className="p-1 hover:opacity-70"
									onClick={onAlertClose}
									type="button"
								>
									<XClose className="size-5 text-red-600" />
								</button>
							) : null}
						</div>
					</div>
				) : null}

				<div className="w-full space-y-1">
					<label
						className="block font-medium text-gray-700 text-sm"
						htmlFor="dateOfLastPreventive"
					>
						Data da última preventiva
					</label>
					<div className="w-full rounded-md [&>div>div]:w-full [&>div]:w-full [&_button]:w-full">
						<DatePicker
							aria-label="Data da última preventiva"
							className="w-full"
							onChange={(date) =>
								onChange(
									"dateOfLastPreventive",
									date ? formatDateValueToDdMmYyyy(date) : ""
								)
							}
							value={parseDateStringToDateValue(formData.dateOfLastPreventive)}
						/>
					</div>
					{fieldErrors.dateOfLastPreventive ? (
						<p className="text-red-600 text-sm">
							{fieldErrors.dateOfLastPreventive}
						</p>
					) : null}
				</div>

				<div className="w-full">
					<Input
						hint={fieldErrors.kilometersOfLastPreventive}
						inputMode="numeric"
						isInvalid={!!fieldErrors.kilometersOfLastPreventive}
						label="Quilometragem da última preventiva"
						onChange={(value) => {
							onChange(
								"kilometersOfLastPreventive",
								parseKilometersInput(value)
							);
						}}
						placeholder="Ex: 15.000 km"
						size="md"
						value={formatKilometersDisplay(formData.kilometersOfLastPreventive)}
					/>
				</div>

				<div className="w-full space-y-1">
					<label
						className="block font-medium text-gray-700 text-sm"
						htmlFor="dateOfNextPreventive"
					>
						Data da próxima preventiva
					</label>
					<div className="w-full rounded-md [&>div>div]:w-full [&>div]:w-full [&_button]:w-full">
						<DatePicker
							aria-label="Data da próxima preventiva"
							className="w-full"
							onChange={(date) =>
								onChange(
									"dateOfNextPreventive",
									date ? formatDateValueToDdMmYyyy(date) : ""
								)
							}
							value={parseDateStringToDateValue(formData.dateOfNextPreventive)}
						/>
					</div>
					{fieldErrors.dateOfNextPreventive ? (
						<p className="text-red-600 text-sm">
							{fieldErrors.dateOfNextPreventive}
						</p>
					) : null}
				</div>
			</div>
		</ModalBase>
	);
};
